import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
} from './guide-ui';

/** Body for "AI root-cause analysis" guide (AI-assisted incident investigation). */
export function AiRootCauseAnalysisGuideBody() {
  return (
    <>
      <Lead>
        AI root-cause analysis uses an AI agent to do what an engineer does during an
        incident: form a hypothesis, query the logs, correlate it with recent
        changes, and follow the trail to a likely cause, but in minutes instead of
        hours. The point is not to remove the human; it is to do the tedious hunting
        so the human verifies a conclusion instead of starting from a blank query
        box. This guide explains how it works.
      </Lead>

      <GuideSection id="what-is-it" title="What is AI root-cause analysis?">
        <P>
          <Term>AI root-cause analysis (RCA)</Term> is AI-assisted investigation of
          why an incident happened. Given a symptom, such as an error spike, a latency jump or a
          failing service, an AI agent searches the relevant logs and telemetry,
          correlates them with deploys and configuration changes, and proposes the
          most likely cause with the evidence that supports it. It turns an open-ended
          hunt into a reviewable hypothesis.
        </P>
      </GuideSection>

      <GuideSection id="how-it-works" title="How it works">
        <OL>
          <LI><Term>Start from a symptom</Term>: an alert, an anomaly, or a natural-language question.</LI>
          <LI><Term>Generate and run queries</Term>: the AI writes queries (in LogPulse, LPQL), runs them, and reads the results.</LI>
          <LI><Term>Correlate with change</Term>: line up the timeline against recent deploys and config changes, the usual culprits.</LI>
          <LI><Term>Follow the trail</Term>: pivot on what it finds, drilling from symptom to contributing log lines.</LI>
          <LI><Term>Propose a cause with evidence</Term>: present a likely root cause and the linked evidence, so a human verifies the conclusion.</LI>
        </OL>
      </GuideSection>

      <GuideSection id="why" title="Why it matters">
        <UL>
          <LI><Term>MTTR</Term>: most incident time is spent hunting; AI compresses that to verification.</LI>
          <LI><Term>Accessibility</Term>: ask in natural language instead of knowing the query syntax up front.</LI>
          <LI><Term>Context</Term>: correlating deploys and changes catches the cause humans often check last.</LI>
          <LI><Term>Consistency</Term>: the same disciplined trail every time, even at 3 a.m.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="trust" title="Keep the human on the conclusion">
        <P>
          AI RCA is most valuable when it is <Term>transparent</Term>: it should show
          the queries it ran and the evidence it found, so you verify rather than
          trust blindly. The human stays the owner of the conclusion. The AI does the
          legwork.
        </P>
        <Callout title="Verify, don't rubber-stamp">
          <p>
            A proposed root cause is a strong starting hypothesis, not a verdict. The
            value is the hours of hunting it saves; the safeguard is that every step is
            shown so you can confirm it.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse does AI root-cause analysis">
        <P>
          The LogPulse <Term>AI Investigator</Term> turns a natural-language question
          into LPQL, runs it on ClickHouse, correlates the timeline with deploys and
          changes, and proposes a likely root cause with linked evidence, showing
          every query so you stay in control of the conclusion. It is included on every
          plan. See{' '}
          <GuideLink href="/ai-log-search">AI log search</GuideLink>,{' '}
          <GuideLink href="/guides/what-is-log-management">what is log management</GuideLink>,
          and, for the security-operations equivalent,{' '}
          <GuideLink href="/guides/ai-soc-analyst">the AI SOC analyst</GuideLink>.
        </P>
      </GuideSection>
    </>
  );
}
